import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import kpiStyles from '../../Document/SubComponents/DocumentKpiCards.module.scss';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import { enrichAssetsWithGroupSaleInfo, isAssetSold } from '../../../Utils/assetSaleDisplayUtils';

const KpiCard = ({ tone, Icon, label, value, alert, direction }) => (
    <article className={[kpiStyles.card, kpiStyles[tone] || '', alert ? kpiStyles.alert : ''].filter(Boolean).join(' ')} dir={direction}>
        <span className={kpiStyles.icon} aria-hidden="true">
            <Icon fontSize="inherit" />
        </span>
        <div className={kpiStyles.copy}>
            <span className={kpiStyles.label}>{label}</span>
            <strong className={kpiStyles.value}>{value}</strong>
        </div>
    </article>
);

KpiCard.propTypes = {
    tone: PropTypes.string.isRequired,
    Icon: PropTypes.elementType.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    alert: PropTypes.bool,
    direction: PropTypes.oneOf(['rtl', 'ltr']),
};

/** Fixed-assets summary: ledger equipment balance vs. register total, plus asset and sold counts. */
const AssetsKpiStrip = ({ totals, assets, assetRelations, title, direction }) => {
    const { t } = useTranslation();

    const counts = useMemo(() => {
        const display = enrichAssetsWithGroupSaleInfo(assets || [], assetRelations || []);
        return { total: display.length, sold: display.filter(isAssetSold).length };
    }, [assets, assetRelations]);

    const equipmentBalance = Number(totals?.equipmentBalance) || 0;
    const totalAssetAmount = Number(totals?.totalAssetAmount) || 0;
    const difference = equipmentBalance - totalAssetAmount;
    const hasTotals = totals != null;

    return (
        <section className={`${kpiStyles.sections} ${kpiStyles.stretch}`} dir={direction}>
            <div className={kpiStyles.panel}>
                <div className={kpiStyles.panelHead}>
                    <h3 className={kpiStyles.panelTitle}>{title}</h3>
                </div>
                <div className={kpiStyles.grid}>
                    <KpiCard
                        tone="equipment"
                        Icon={PrecisionManufacturingOutlinedIcon}
                        label={t('TotalEquipmentBalance')}
                        value={hasTotals ? beautifyNumber(equipmentBalance) : '—'}
                        direction={direction}
                    />
                    <KpiCard
                        tone="total"
                        Icon={Inventory2OutlinedIcon}
                        label={t('TotalAssetAmount')}
                        value={hasTotals ? beautifyNumber(totalAssetAmount) : '—'}
                        direction={direction}
                    />
                    <KpiCard
                        tone="diff"
                        Icon={CompareArrowsOutlinedIcon}
                        label={t('Difference')}
                        value={hasTotals ? beautifyNumber(difference) : '—'}
                        alert={hasTotals && Math.abs(difference) >= 0.5}
                        direction={direction}
                    />
                    <KpiCard
                        tone="income"
                        Icon={CategoryOutlinedIcon}
                        label={t('AssetsCount')}
                        value={String(counts.total)}
                        direction={direction}
                    />
                    <KpiCard
                        tone="expense"
                        Icon={SellOutlinedIcon}
                        label={t('SoldAssets')}
                        value={String(counts.sold)}
                        direction={direction}
                    />
                </div>
            </div>
        </section>
    );
};

AssetsKpiStrip.propTypes = {
    totals: PropTypes.shape({
        equipmentBalance: PropTypes.number,
        totalAssetAmount: PropTypes.number,
    }),
    assets: PropTypes.array,
    assetRelations: PropTypes.array,
    title: PropTypes.string.isRequired,
    direction: PropTypes.oneOf(['rtl', 'ltr']),
};

export default AssetsKpiStrip;
